import React, { useRef, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Loader2, Download, FileText, FileAudio, FileVideo, File as FileIcon, Image as ImageIcon } from "lucide-react";
import { PinNoteButton } from "./NotesPanel";
import { mediaUrl, filesApi } from "../lib/api";

// Fetch a user-uploaded file (auth-gated) and render a thumbnail via blob URL.
const UploadedImageInline = ({ fileId, filename }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let blobUrl = null;
    filesApi
      .fetchBlobUrl(fileId)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        blobUrl = u;
        setUrl(u);
      })
      .catch(() => {
        /* ignore */
      });
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [fileId]);
  if (!url) {
    return (
      <div className="mb-2 rounded-lg border border-white/10 bg-black/30 h-32 flex items-center justify-center">
        <Loader2 className="h-4 w-4 animate-spin text-white/60" />
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={filename || "attached image"}
      className="mb-2 rounded-lg border border-white/10 max-h-64 w-auto object-cover bg-black/30"
      data-testid="inline-uploaded-image"
    />
  );
};

const iconForCtype = (ctype = "", name = "") => {
  const c = (ctype || "").toLowerCase();
  if (c.startsWith("image/")) return ImageIcon;
  if (c.startsWith("audio/")) return FileAudio;
  if (c.startsWith("video/")) return FileVideo;
  if (c === "application/pdf" || c.startsWith("text/") || /\.(md|txt|json|csv|pdf|log|xml|html?)$/i.test(name)) return FileText;
  return FileIcon;
};

const humanSize = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const InlineAttachment = ({ attachment }) => {
  if (!attachment) return null;
  if (attachment.kind === "image" && attachment.file_url) {
    return (
      <a
        href={mediaUrl(attachment.file_url)}
        target="_blank"
        rel="noreferrer"
        className="block mb-2 rounded-lg overflow-hidden border border-white/10 relative group/att"
        data-testid="inline-image"
      >
        <img
          src={mediaUrl(attachment.file_url)}
          alt={attachment.prompt || "generated image"}
          className="max-h-64 w-auto object-cover bg-black/30"
        />
        <span className="absolute top-1.5 right-1.5 h-7 w-7 rounded-md bg-black/60 opacity-0 group-hover/att:opacity-100 flex items-center justify-center transition-opacity">
          <Download className="h-3.5 w-3.5 text-white/80" />
        </span>
      </a>
    );
  }
  if (attachment.kind === "video") {
    if (!attachment.file_url) {
      return (
        <div
          className="mb-2 rounded-lg border border-white/10 bg-black/30 h-32 flex items-center justify-center gap-2 text-xs text-white/60"
          data-testid="inline-video-pending"
        >
          <Loader2 className="h-4 w-4 animate-spin text-blue-300" />
          Rendering video…
        </div>
      );
    }
    return (
      <video
        src={mediaUrl(attachment.file_url)}
        controls
        className="mb-2 rounded-lg border border-white/10 max-h-72 w-auto bg-black/30"
        data-testid="inline-video"
      />
    );
  }
  // User-attached file from the Files library
  if (attachment.kind === "file" && attachment.file_id) {
    const ctype = attachment.content_type || "";
    const name = attachment.filename || "file";
    const Icon = iconForCtype(ctype, name);
    if (ctype.startsWith("image/")) {
      return <UploadedImageInline fileId={attachment.file_id} filename={name} />;
    }
    return (
      <div
        className="mb-2 flex items-center gap-2.5 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2"
        data-testid="inline-file-chip"
      >
        <Icon className="h-4 w-4 text-blue-300 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-xs text-white/90 truncate">{name}</div>
          <div className="text-[10px] text-white/45">
            {ctype || "file"}
            {attachment.size ? ` · ${humanSize(attachment.size)}` : ""}
          </div>
        </div>
      </div>
    );
  }
  return null;
};


export const TranscriptPanel = ({
  messages,
  liveUser,
  liveAssistant,
  open,
  onToggle,
  sessionId,
}) => {
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, liveUser, liveAssistant, open]);

  return (
    <div className="w-full max-w-2xl mx-auto px-4 flex-1 min-h-0 flex flex-col">
      <div className="flex justify-center mb-1.5 shrink-0">
        <button
          data-testid="transcript-toggle-btn"
          onClick={onToggle}
          className="text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-blue-300 flex items-center gap-1.5 transition-colors"
        >
          {open ? "Hide transcript" : "Show transcript"}
          <ChevronDown
            className={`h-3 w-3 transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>
      </div>

      <AnimatePresence initial={false} mode="wait">
        {open && (
          <motion.div
            data-testid="transcript-panel"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex-1 min-h-0 flex"
          >
            <div className="glass-strong rounded-2xl px-4 py-2.5 flex-1 min-h-0 flex flex-col w-full">
              <div
                ref={scrollRef}
                className="overflow-y-auto flex-1 min-h-0 space-y-2 pr-2"
              >
              {messages.length === 0 && !liveUser && !liveAssistant && (
                <div className="text-xs text-white/40 text-center py-5">
                  Your conversation will appear here.
                </div>
              )}
              {messages.map((m) => (
                <div
                  key={m.id}
                  data-testid={`msg-${m.role}`}
                  className={`group flex items-start gap-1.5 ${
                    m.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  {m.role === "assistant" && (
                    <PinNoteButton text={m.text} sessionId={sessionId} />
                  )}
                  <div
                    className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed ${
                      m.role === "user"
                        ? "bg-blue-600/25 border border-blue-400/30 text-white"
                        : "bg-white/5 border border-white/10 text-white/90"
                    }`}
                  >
                    {m.role === "assistant" && (
                      <div className="text-[9px] uppercase tracking-widest text-blue-300/70 mb-0.5">
                        Glitch
                      </div>
                    )}
                    {m.attachment && <InlineAttachment attachment={m.attachment} />}
                    {m.text}
                  </div>
                  {m.role === "user" && (
                    <PinNoteButton text={m.text} sessionId={sessionId} />
                  )}
                </div>
              ))}
              {liveUser && (
                <div className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl px-3.5 py-2 text-[13px] bg-blue-600/15 border border-blue-400/20 text-white/80 italic">
                    {liveUser}
                  </div>
                </div>
              )}
              {liveAssistant && (
                <div className="flex justify-start">
                  <div className="max-w-[85%] rounded-2xl px-3.5 py-2 text-[13px] bg-white/[0.03] border border-white/10 text-white/80">
                    <div className="text-[9px] uppercase tracking-widest text-blue-300/70 mb-0.5">
                      Glitch
                    </div>
                    <span className="shimmer-text">{liveAssistant}</span>
                  </div>
                </div>
              )}
            </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
